/* Product gallery — "Gallery with slider" style
   Defines <product-gallery-slider> (main slide track, arrows, drag) and
   <product-gallery-thumbs> (thumbnail row, syncs with the main slider).
   Zoom/expand is NOT handled here — each image is wrapped in a
   `.MagicZoom` link (see snippet-product-media-slider.liquid) so the
   theme's existing MagicZoom app opens the exact same popup it uses
   everywhere else on the site.
   Fully independent from page-product.js / product-slider-component. */
(function () {
  "use strict";

  if (window.customElements.get("product-gallery-slider")) return;

  class ProductGallerySlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector(".pgs-track");
      this.slides = Array.from(this.querySelectorAll(".pgs-slide"));
      this.prevBtn = this.querySelector(".pgs-prev");
      this.nextBtn = this.querySelector(".pgs-next");
      this.hiddenSlides ||= new Map();
      this.index = 0;
      this.isDragging = false;
      this.dragStartX = 0;
      this.dragDeltaX = 0;
      this.suppressNextClick = false;

      this.prevBtn && this.prevBtn.addEventListener("click", () => this.go(this.index - 1));
      this.nextBtn && this.nextBtn.addEventListener("click", () => this.go(this.index + 1));

      this.bindDrag();
      this.update();
    }

    go(index) {
      const count = this.slides.length;
      if (count < 1) return;
      // Loop around in both directions — next on the last slide goes to the
      // first, prev on the first slide goes to the last.
      this.index = ((index % count) + count) % count;
      this.update();
      this.dispatchEvent(new CustomEvent("gallery:change", { bubbles: true, detail: { index: this.index } }));
    }

    update() {
      if (!this.track) return;
      const selected = this.slides[this.index];
      const restoreFocus = this.slides.some((slide, index) => index !== this.index && slide.contains(document.activeElement));
      const previous = this.hiddenSlides.get(selected);
      if (previous) {
        selected.inert = previous.inert;
        if (previous.ariaHidden === null) selected.removeAttribute("aria-hidden");
        else selected.setAttribute("aria-hidden", previous.ariaHidden);
        this.hiddenSlides.delete(selected);
      }
      this.track.style.transform = `translateX(${this.index * -100}%)`;
      this.scrollLeft = 0;
      if (restoreFocus) {
        const controls = selected ? Array.from(selected.querySelectorAll('a[href],button,input:not([type="hidden"]),select,textarea,iframe,video[controls],audio[controls],[tabindex]')) : [];
        const target = [...controls, this.prevBtn, this.nextBtn].find(element => element && element.tabIndex >= 0 && !element.matches(":disabled") && !element.closest('[inert],[hidden],[aria-hidden="true"]') && element.getClientRects().length && getComputedStyle(element).visibility !== "hidden");
        target?.focus({preventScroll: true});
      }
      this.slides.forEach((slide, index) => {
        if (index === this.index) return;
        if (!this.hiddenSlides.has(slide)) this.hiddenSlides.set(slide, {inert: slide.inert, ariaHidden: slide.getAttribute("aria-hidden")});
        slide.inert = true;
        slide.setAttribute("aria-hidden", "true");
      });
    }

    bindDrag() {
      if (!this.track) return;

      // A real drag shouldn't also open the MagicZoom popup underneath the
      // pointer once the user releases — swallow the click that follows it.
      this.track.addEventListener(
        "click",
        (e) => {
          if (!this.suppressNextClick) return;
          this.suppressNextClick = false;
          e.preventDefault();
          e.stopPropagation();
        },
        true
      );

      if (this.slides.length < 2) return;

      const start = (clientX) => {
        this.isDragging = true;
        this.dragStartX = clientX;
        this.dragDeltaX = 0;
        this.track.classList.add("is-dragging");
      };

      const move = (clientX) => {
        if (!this.isDragging) return;
        this.dragDeltaX = clientX - this.dragStartX;
        this.track.style.transform = `translateX(calc(${this.index * -100}% + ${this.dragDeltaX}px))`;
      };

      const end = () => {
        if (!this.isDragging) return;
        this.isDragging = false;
        this.track.classList.remove("is-dragging");

        if (Math.abs(this.dragDeltaX) > 5) this.suppressNextClick = true;

        const threshold = this.clientWidth * 0.15;
        if (this.dragDeltaX > threshold) {
          this.go(this.index - 1);
        } else if (this.dragDeltaX < -threshold) {
          this.go(this.index + 1);
        } else {
          this.update();
        }
        this.dragDeltaX = 0;
      };

      this.track.addEventListener("pointerdown", (e) => {
        if (e.pointerType === "mouse" && e.button !== 0) return;
        if (e.target.closest(".pgs-nav")) return;
        start(e.clientX);
      });
      this.track.addEventListener("pointermove", (e) => move(e.clientX));
      this.track.addEventListener("pointerup", end);
      this.track.addEventListener("pointercancel", end);
      this.track.addEventListener("pointerleave", () => this.isDragging && end());
    }
  }

  class ProductGalleryThumbs extends HTMLElement {
    connectedCallback() {
      this.buttons = Array.from(this.querySelectorAll(".pgs-thumb"));
      const wrapper = this.closest(".pgs");
      this.main = wrapper ? wrapper.querySelector("product-gallery-slider") : null;
      if (!this.main) return;

      this.buttons.forEach((btn, i) => btn.addEventListener("click", () => this.main.go(i)));
      this.main.addEventListener("gallery:change", (e) => this.setActive(e.detail.index));
      this.setActive(this.main.index || 0);
    }

    setActive(index) {
      this.buttons.forEach((btn, i) => {
        btn.classList.toggle("is-active", i === index);
        if (i === index) btn.setAttribute("aria-current", "true");
        else btn.removeAttribute("aria-current");
      });
      const active = this.buttons[index];
      active && active.scrollIntoView && active.scrollIntoView({ behavior: "smooth", inline: "nearest", block: "nearest" });
    }
  }

  window.customElements.define("product-gallery-slider", ProductGallerySlider);
  window.customElements.define("product-gallery-thumbs", ProductGalleryThumbs);
})();
